import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ClosetItemSummary, ModelClosetResponse } from '@fetmode/types';
import { CLOSET_CATEGORIES } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';

export function ModelClosetCard(): ReactElement {
  const { api } = useAuth();
  const [items, setItems] = useState<ClosetItemSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result: ModelClosetResponse = await api.getMyCloset();
    setItems(result.items);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.closet.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleToggle(item: ClosetItemSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      const result = await api.updateMyCloset({
        items: [{ catalogKey: item.catalogKey, enabled: !item.enabled }],
      });
      setItems(result.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.closet.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.closet.loading')}</p>;
  }

  if (items.length === 0 && error) {
    return <p className="fm-error">{error}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.closet.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.closet.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      {CLOSET_CATEGORIES.map((category) => {
        const group = items.filter((item) => item.category === category);
        if (group.length === 0) {
          return null;
        }
        return (
          <div key={category} style={{ marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', color: 'var(--fm-color-text-muted)' }}>
              {t(`common.studio.closet.category.${category}`)}
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {group.map((item) => (
                <li
                  key={item.catalogKey}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.4rem 0',
                    borderBottom: '1px solid var(--fm-color-border)',
                    opacity: item.enabled ? 1 : 0.55,
                  }}
                >
                  <span aria-hidden style={{ fontSize: '1.15rem', width: '1.5rem', textAlign: 'center' }}>
                    {item.icon}
                  </span>
                  <span style={{ flex: 1, fontWeight: 600 }}>{item.label}</span>
                  <label
                    style={{
                      display: 'inline-flex',
                      gap: '0.35rem',
                      alignItems: 'center',
                      fontSize: '0.8125rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      disabled={isSaving}
                      onChange={() => {
                        void handleToggle(item);
                      }}
                    />
                    {t('common.studio.closet.enabled')}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
